import { useCallback, useState } from 'react';
import { Toast } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import {
  meetingNotice,
  useMeetingsChannelActions,
  type MeetingDecision,
  type MeetingNotice,
  type UpcomingMeeting,
} from '../../../lib/meetings-channel';

/** How long a Remove's Undo stays on screen: the server holds her leave for 5 s (`leaveAt`). */
export const MEETING_UNDO_MS = 5_000;

interface Shown {
  notice: MeetingNotice;
  meeting: UpcomingMeeting;
  decision: MeetingDecision;
}

/**
 * One decision on one meeting, and the toast its answer shows: Undo after a Remove during the meeting (it sends `keep`), Try Again
 * after a failed Add, the reason after a refusal. Used by the Meetings list, the calendar card and the join chip,
 * so all three answer the same way.
 */
export function useMeetingDecision() {
  const { decide } = useMeetingsChannelActions();
  const [shown, setShown] = useState<Shown | null>(null);
  // Stable, because the toast's auto-hide timer restarts whenever its `onClose` changes.
  const dismiss = useCallback(() => setShown(null), []);

  const send = (meeting: UpcomingMeeting, decision: MeetingDecision) => {
    setShown(null);
    decide.mutate(
      { meeting, decision },
      {
        onSuccess: (answer) => {
          const notice = meetingNotice(decision, answer);
          setShown(notice ? { notice, meeting, decision } : null);
        },
        onError: () => {
          const notice = meetingNotice(decision, { kind: 'failed' });
          setShown(notice ? { notice, meeting, decision } : null);
        },
      },
    );
  };

  return { send, busy: decide.isLoading, shown, dismiss };
}

export function MeetingDecisionToast({
  decision,
  biPage,
}: {
  decision: ReturnType<typeof useMeetingDecision>;
  /** The host's page_name, so Undo / Retry clicks land on the surface the toast showed on. */
  biPage: string;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const agent = useAgentName().name;
  const { shown, send, dismiss } = decision;
  const notice = shown?.notice;
  const action =
    shown && notice?.undo
      ? {
        label: t('connectors.meetings.action.undo'),
        onClick: () => {
          logPageClickBi({ page_name: biPage, button_name: 'meeting_toast_undo' });
          send(shown.meeting, 'keep');
        },
      }
      : shown && notice?.retry
      ? {
        label: t('common.retry'),
        onClick: () => {
          logPageClickBi({ page_name: biPage, button_name: 'meeting_toast_retry' });
          send(shown.meeting, shown.decision);
        },
      }
      : undefined;
  return (
    <Toast
      open={Boolean(notice)}
      message={notice ? t(notice.message, { agent }) : ''}
      severity={notice?.severity === 'error' ? 'error' : 'info'}
      autoHideDuration={notice?.undo ? MEETING_UNDO_MS : 6_000}
      action={action}
      onClose={dismiss}
    />
  );
}
